import { useEffect, useRef } from 'react';

export const IDLE_WARNING_MS = 50_000;
export const IDLE_TIMEOUT_MS = 60_000;

const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = ['pointerdown', 'touchstart', 'keydown'];

type IdleTimerOptions = {
  enabled: boolean;
  onWarning: () => void;
  onReset: () => void;
  onActivity?: () => void;
};

/**
 * Fires onWarning at 50s of inactivity and onReset at 60s. Resets only on real
 * user input (pointer/touch/key) — never on scroll or tab-visibility changes.
 * Pass `enabled: false` to pause (e.g. on the welcome/attract screen).
 */
export function useIdleTimer({ enabled, onWarning, onReset, onActivity }: IdleTimerOptions): void {
  const warnTimer = useRef<ReturnType<typeof setTimeout>>();
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();
  // Keep latest callbacks without re-arming listeners every render.
  const cbs = useRef({ onWarning, onReset, onActivity });
  cbs.current = { onWarning, onReset, onActivity };

  useEffect(() => {
    if (!enabled) return;

    const arm = () => {
      warnTimer.current = setTimeout(() => cbs.current.onWarning(), IDLE_WARNING_MS);
      resetTimer.current = setTimeout(() => cbs.current.onReset(), IDLE_TIMEOUT_MS);
    };
    const clear = () => {
      clearTimeout(warnTimer.current);
      clearTimeout(resetTimer.current);
    };
    const onInput = () => {
      cbs.current.onActivity?.();
      clear();
      arm();
    };

    arm();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onInput, { passive: true }));
    return () => {
      clear();
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onInput));
    };
  }, [enabled]);
}
